import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TYPES_SANS_REPONSE, VALEUR_PHOTO, itemsDe, cleItem, regrouperEtapes, valeurDe, valeursPlates,
  parcourirSection, champsManquants, reponsesVisibles, alertesDeclenchees, retirerInstance,
  formaterTelephone, courrielValide, partiesDate, composerDate, datesInvalides,
} from '../utils/autoInspectionEtapes';
import { evaluerConditionsAffichage } from '../utils/conditionsAffichage';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

/**
 * AutoInspectionParEtapes — formulaire PUBLIC d'auto-inspection en plusieurs étapes
 * (réglage Prévention `auto_declaration_par_etapes`, ex. programme de Sutton).
 *
 * Rendu par AutoDeclarationPublique quand la config publique porte `par_etapes`.
 * Les étapes, questions, conditions et articles viennent de la GRILLE (aucun libellé
 * de question codé ici). Seuls les champs d'identification (nom, adresse, courriel,
 * téléphones) sont propres au formulaire, en tête de la première étape.
 *
 * Envoie au même endpoint public que le formulaire historique, avec les réponses au
 * format natif des inspections (section_{s}_item_{i}) — le serveur recalcule la
 * visibilité et la conformité (services/auto_declaration.py).
 *
 * Bilingue : textes de l'application dans i18n (`autoInspection.*`), textes du service
 * (questions, descriptions, articles…) via `config.traductions_en`. Les valeurs
 * enregistrées restent en FRANÇAIS (conformité et articles les comparent).
 */
const COULEURS = {
  fond: '#f8fafc', carte: '#ffffff', bord: '#e2e8f0', texte: '#0f172a',
  sousTexte: '#64748b', primaire: '#dc2626', vert: '#16a34a', orange: '#f59e0b',
  accent: '#2563eb',
};

const IDENTITE_VIDE = {
  prenom: '', nom: '', numero_civique: '', rue: '', courriel: '', telephone: '', telephone_urgence: '',
};

// Brouillon : progression gardée sur l'appareil du citoyen (sans les photos, trop
// lourdes). Le stockage peut être indisponible (navigation privée) → try/catch partout.
const lireBrouillon = (cle) => {
  try {
    const b = JSON.parse(window.localStorage.getItem(cle) || 'null');
    return b && typeof b === 'object' ? b : null;
  } catch { return null; }
};
const effacerBrouillon = (cle) => {
  try { window.localStorage.removeItem(cle); } catch { /* stockage indisponible */ }
};
const brouillonNonVide = (b) => !!b && (Object.keys(b.reponses || {}).length > 0
  || Object.values(b.identite || {}).some((v) => String(v || '').trim()));

// Photos : réduites côté client (basse résolution demandée par le service).
const PHOTO_COTE_MAX = 1600;
const PHOTO_TYPES = ['image/jpeg', 'image/png'];

const reduireImage = (fichier) => new Promise((resolve, reject) => {
  const lecteur = new FileReader();
  lecteur.onerror = () => reject(new Error('lecture'));
  lecteur.onload = () => {
    const img = new Image();
    img.onerror = () => reject(new Error('image'));
    img.onload = () => {
      const ratio = Math.min(1, PHOTO_COTE_MAX / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * ratio);
      canvas.height = Math.round(img.height * ratio);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', 0.75));
    };
    img.src = lecteur.result;
  };
  lecteur.readAsDataURL(fichier);
});

// Texte avec liens cliquables (les références de Sutton pointent vers des pages externes).
const TexteAvecLiens = ({ texte }) => (
  <>
    {String(texte || '').split(/(https?:\/\/\S+)/g).map((morceau, i) => (
      /^https?:\/\//.test(morceau)
        ? <a key={i} href={morceau} target="_blank" rel="noopener noreferrer"
          style={{ color: COULEURS.accent, wordBreak: 'break-all' }}>{morceau}</a>
        : <React.Fragment key={i}>{morceau}</React.Fragment>
    ))}
  </>
);

// Schémas de repérage (remplacent les images du formulaire source).
const VERT = '#86dcae';
const MAISON = '#c9b49a';
const RUE = '#e5e7eb';
const SCHEMAS = {
  // Maison divisée en 4 quadrants : B | C à l'arrière, A | D côté rue (annexe B).
  chambre_quadrants: (t) => (
    <svg viewBox="0 0 240 200" role="img" aria-label={t('autoInspection.schemaQuadrants')}
      style={{ width: '100%', maxWidth: 260 }}>
      <rect x="0" y="0" width="240" height="160" fill={VERT} />
      <rect x="30" y="20" width="180" height="120" fill={MAISON} stroke="#6b5b4b" strokeWidth="1.5" />
      <line x1="120" y1="20" x2="120" y2="140" stroke="#6b5b4b" strokeWidth="1.5" />
      <line x1="30" y1="80" x2="210" y2="80" stroke="#6b5b4b" strokeWidth="1.5" />
      {[['B', 75, 59], ['C', 165, 59], ['A', 75, 119], ['D', 165, 119]].map(([l, x, y]) => (
        <text key={l} x={x} y={y} textAnchor="middle" fontSize="26" fill="#111827">{l}</text>
      ))}
      <rect x="0" y="160" width="240" height="40" fill={RUE} />
      <text x="120" y="188" textAnchor="middle" fontSize="20" fill="#111827">{t('autoInspection.rue')}</text>
    </svg>
  ),
  // Maison vue de haut : 2 à gauche, 3 à l'arrière, 4 à droite, 5 côté rue (annexe B).
  reservoir_cotes: (t) => (
    <svg viewBox="0 0 240 220" role="img" aria-label={t('autoInspection.schemaCotes')}
      style={{ width: '100%', maxWidth: 260 }}>
      <rect x="0" y="0" width="240" height="180" fill={VERT} />
      <rect x="40" y="30" width="160" height="120" fill={MAISON} stroke="#6b5b4b" strokeWidth="1.5" />
      {[['3', 120, 22], ['2', 20, 98], ['4', 220, 98], ['5', 120, 172]].map(([l, x, y]) => (
        <text key={l} x={x} y={y} textAnchor="middle" fontSize="20" fill="#111827">{l}</text>
      ))}
      <rect x="0" y="180" width="240" height="40" fill={RUE} />
      <text x="120" y="208" textAnchor="middle" fontSize="20" fill="#111827">{t('autoInspection.rue')}</text>
    </svg>
  ),
};

const vide = (v) => v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);

const AutoInspectionParEtapes = ({ config, tenantSlug, backend }) => {
  const { t, i18n } = useTranslation();
  const enAnglais = i18n.language === 'en';
  // Texte saisi par le service → version anglaise si disponible, sinon français.
  const traduire = (texte) => {
    if (!enAnglais || !texte) return texte;
    const cle = String(texte).trim();
    const dict = (config && config.traductions_en) || {};
    if (dict[cle]) return dict[cle];
    const defauts = t('autoDeclaration.optionsDefaut', { returnObjects: true }) || {};
    return defauts[cle] || texte;
  };
  const sections = Array.isArray(config?.grille?.sections) ? config.grille.sections : [];
  const etapes = regrouperEtapes(sections);
  const nbEtapes = etapes.length;

  const cleBrouillon = `pfm-auto-inspection-brouillon:${tenantSlug}`;
  const [brouillon] = useState(() => lireBrouillon(cleBrouillon));
  const [restaure, setRestaure] = useState(() => brouillonNonVide(brouillon));
  const [etapeIdx, setEtapeIdx] = useState(() => Math.min(Math.max(0, parseInt(brouillon?.etapeIdx, 10) || 0),
    Math.max(0, nbEtapes - 1)));
  const [identite, setIdentite] = useState(() => ({ ...IDENTITE_VIDE, ...(brouillon?.identite || {}) }));
  const [batimentChoisi, setBatimentChoisi] = useState(() => brouillon?.batimentChoisi || null);
  const [reponses, setReponses] = useState(() => brouillon?.reponses || {});
  const [instances, setInstances] = useState(() => brouillon?.instances || {});   // { sIdx: nombre d'instances }
  const [photos, setPhotos] = useState({});         // { cle: dataURL }
  const [erreurs, setErreurs] = useState({});       // { cle|champ: message }
  const [erreurPhoto, setErreurPhoto] = useState('');
  const [neRienRemplir, setNeRienRemplir] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [envoye, setEnvoye] = useState(false);
  const [erreur, setErreur] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  // Fenêtre « Ajouter - Ajout » d'une section répétable :
  // { s, k (null = nouvelle entrée), valeurs: { iIdx: valeur }, erreurs: { iIdx: cle } }
  const [fenetre, setFenetre] = useState(null);
  // Tableau des entrées sur grand écran ; une fiche par entrée sur téléphone (les
  // en-têtes bilingues de Sutton sont trop longs pour 3 colonnes à 375 px).
  const [ecranLarge, setEcranLarge] = useState(() => {
    try { return window.matchMedia('(min-width: 640px)').matches; } catch { return true; }
  });
  useEffect(() => {
    let mq;
    try { mq = window.matchMedia('(min-width: 640px)'); } catch { return undefined; }
    const maj = (e) => setEcranLarge(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', maj); else mq.addListener(maj);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', maj); else mq.removeListener(maj); };
  }, []);
  const debounceRef = useRef(null);
  const hautRef = useRef(null);

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  // Sauvegarde du brouillon à chaque changement (photos exclues).
  useEffect(() => {
    if (envoye) return;
    const sansPhotos = Object.fromEntries(Object.entries(reponses).filter(([, v]) => v !== VALEUR_PHOTO));
    try {
      window.localStorage.setItem(cleBrouillon, JSON.stringify({
        etapeIdx, identite, batimentChoisi, reponses: sansPhotos, instances,
      }));
    } catch { /* stockage indisponible : le formulaire fonctionne sans brouillon */ }
  }, [cleBrouillon, envoye, etapeIdx, identite, batimentChoisi, reponses, instances]);

  const recommencer = () => {
    effacerBrouillon(cleBrouillon);
    setRestaure(false);
    setEtapeIdx(0); setIdentite(IDENTITE_VIDE); setBatimentChoisi(null);
    setReponses({}); setInstances({}); setPhotos({}); setErreurs({}); setErreur('');
  };

  const allerEnHaut = () => {
    if (hautRef.current && hautRef.current.scrollIntoView) hautRef.current.scrollIntoView({ behavior: 'smooth' });
  };

  const majIdentite = (champ, valeur) => {
    setIdentite((p) => ({ ...p, [champ]: valeur }));
    setErreurs((p) => { const n = { ...p }; delete n[champ]; return n; });
  };

  // Autocomplétion d'adresse (adresse-only), sur « numéro + rue ».
  const majAdresse = (champ, valeur) => {
    majIdentite(champ, valeur);
    setBatimentChoisi(null);
    const suivante = { ...identite, [champ]: valeur };
    const q = `${suivante.numero_civique} ${suivante.rue}`.trim();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.length < 3 || !suivante.rue.trim()) { setSuggestions([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await fetch(
          `${backend}/api/${tenantSlug}/public/auto-declaration/adresses?q=${encodeURIComponent(q)}`);
        if (!r.ok) { setSuggestions([]); return; }
        const data = await r.json();
        setSuggestions(data.items || []);
      } catch { setSuggestions([]); }
    }, 300);
  };

  const choisirAdresse = (item) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const m = String(item.adresse_civique || '').match(/^(\S+)\s+(.*)$/);
    setIdentite((p) => ({ ...p, numero_civique: m ? m[1] : p.numero_civique, rue: m ? m[2] : (item.adresse_civique || '') }));
    setErreurs((p) => { const n = { ...p }; delete n.numero_civique; delete n.rue; return n; });
    setBatimentChoisi(item.id);
    setSuggestions([]);
  };

  const repondre = (cle, valeur) => {
    setReponses((p) => ({ ...p, [cle]: valeur }));
    setErreurs((p) => { const n = { ...p }; delete n[cle]; return n; });
  };

  const choisirPhoto = async (cle, fichier) => {
    setErreurPhoto('');
    if (!fichier) return;
    if (!PHOTO_TYPES.includes(fichier.type)) {
      setErreurPhoto('autoInspection.formatPhoto');
      return;
    }
    try {
      const dataUrl = await reduireImage(fichier);
      setPhotos((p) => ({ ...p, [cle]: dataUrl }));
      repondre(cle, VALEUR_PHOTO);
    } catch {
      setErreurPhoto('autoInspection.photoIllisible');
    }
  };

  const retirerPhoto = (cle) => {
    setPhotos((p) => { const n = { ...p }; delete n[cle]; return n; });
    setReponses((p) => { const n = { ...p }; delete n[cle]; return n; });
  };

  const supprimerInstance = (s, k) => {
    const nb = instances[s] || 0;
    setReponses((p) => retirerInstance(p, s, k, nb));
    setInstances((p) => ({ ...p, [s]: Math.max(0, nb - 1) }));
  };

  // ---- Fenêtre d'ajout / de modification d'une entrée répétable ----
  const ouvrirFenetre = (s, k = null) => {
    const valeurs = {};
    if (k !== null) {
      itemsDe(sections[s]).forEach((_, i) => {
        const v = reponses[cleItem(s, i, k)];
        if (!vide(v)) valeurs[i] = v;
      });
    }
    setFenetre({ s, k, valeurs, erreurs: {} });
  };

  const itemsVisiblesFenetre = (f) => {
    const valeurs = { ...valeursPlates(sections, reponses) };
    itemsDe(sections[f.s]).forEach((it, i) => { if (it && it.id) valeurs[it.id] = f.valeurs[i]; });
    return itemsDe(sections[f.s]).map((it, i) => [it, i]).filter(([it]) => it && typeof it === 'object'
      && evaluerConditionsAffichage(it.conditions_affichage, valeurs));
  };

  const validerFenetre = () => {
    const f = fenetre;
    const visibles = itemsVisiblesFenetre(f);
    const errs = {};
    visibles.forEach(([it, i]) => {
      if (it.obligatoire && !TYPES_SANS_REPONSE.has(it.type) && vide(f.valeurs[i])) errs[i] = 'champObligatoire';
    });
    if (Object.keys(errs).length) { setFenetre({ ...f, erreurs: errs }); return; }
    const nb = instances[f.s] || 0;
    const k = f.k === null ? nb : f.k;
    const gardes = new Set(visibles.map(([, i]) => i));
    setReponses((p) => {
      const n = { ...p };
      itemsDe(sections[f.s]).forEach((_, i) => {
        const c = cleItem(f.s, i, k);
        if (gardes.has(i) && !vide(f.valeurs[i])) n[c] = f.valeurs[i]; else delete n[c];
      });
      return n;
    });
    if (f.k === null) setInstances((p) => ({ ...p, [f.s]: nb + 1 }));
    setErreurs((p) => Object.fromEntries(Object.entries(p).filter(([c]) => !c.startsWith(`section_${f.s}_inst_`))));
    setFenetre(null);
  };

  // ---- Validation de l'étape courante (champs VISIBLES seulement) ----
  const validerEtape = () => {
    const errs = {};
    if (etapeIdx === 0) {
      ['prenom', 'nom', 'numero_civique', 'rue', 'courriel', 'telephone', 'telephone_urgence'].forEach((c) => {
        if (!String(identite[c] || '').trim()) errs[c] = 'champObligatoire';
      });
      if (identite.courriel.trim() && !courrielValide(identite.courriel)) errs.courriel = 'courrielInvalide';
      ['telephone', 'telephone_urgence'].forEach((c) => {
        if (identite[c].trim() && identite[c].replace(/\D/g, '').length !== 10) errs[c] = 'telephoneInvalide';
      });
    }
    const indices = etapes[etapeIdx]?.sections || [];
    champsManquants(sections, indices, reponses, instances)
      .forEach((cle) => { errs[cle] = 'champObligatoire'; });
    datesInvalides(sections, indices, reponses, instances)
      .forEach((cle) => { errs[cle] = 'dateInvalide'; });
    setErreurs(errs);
    return Object.keys(errs).length === 0;
  };

  const suivant = () => {
    if (!validerEtape()) { setErreur('autoInspection.remplirObligatoires'); return; }
    setErreur('');
    setEtapeIdx((i) => Math.min(nbEtapes - 1, i + 1));
    allerEnHaut();
  };

  const precedent = () => {
    setErreur(''); setErreurs({});
    setEtapeIdx((i) => Math.max(0, i - 1));
    allerEnHaut();
  };

  const envoyer = async () => {
    if (!validerEtape()) { setErreur('autoInspection.remplirObligatoires'); return; }
    setErreur(''); setEnvoi(true);
    const visibles = reponsesVisibles(sections, reponses, instances);
    const photosVisibles = Object.fromEntries(
      Object.entries(photos).filter(([cle]) => visibles[cle] === VALEUR_PHOTO));
    const nomComplet = `${identite.prenom.trim()} ${identite.nom.trim()}`.trim();
    try {
      const r = await fetch(`${backend}/api/${tenantSlug}/public/auto-declaration`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          occupant_nom: nomComplet,
          // Le formulaire ne distingue pas le propriétaire : le nom du répondant sert au
          // rapprochement ; s'il ne correspond pas, la déclaration va « À rattacher ».
          proprietaire_nom: nomComplet,
          adresse: `${identite.numero_civique.trim()} ${identite.rue.trim()}`.trim(),
          ville: config.ville_defaut || '',
          code_postal: '',
          batiment_id_choisi: batimentChoisi,
          courriel: identite.courriel.trim(),
          telephone: identite.telephone,
          telephone_urgence: identite.telephone_urgence,
          reponses_grille: visibles,
          photos: photosVisibles,
          ne_pas_remplir: neRienRemplir,
          langue: enAnglais ? 'en' : 'fr',
        }),
      });
      if (r.status === 429) { setErreur('autoDeclaration.erreurTropDeTentatives'); return; }
      if (!r.ok) { setErreur('autoDeclaration.erreurGenerique'); return; }
      effacerBrouillon(cleBrouillon);
      setEnvoye(true);
      allerEnHaut();
    } catch {
      setErreur('autoDeclaration.erreurConnexion');
    } finally { setEnvoi(false); }
  };

  // ---- Rendu ----
  const carte = (contenu, cle) => (
    <div key={cle} style={{ background: COULEURS.carte, border: `1px solid ${COULEURS.bord}`,
      borderRadius: 12, padding: '1.25rem', marginBottom: '1rem' }}>{contenu}</div>
  );

  const styleSaisie = (enErreur) => ({
    width: '100%', padding: '0.6rem 0.75rem', fontSize: '1rem', minHeight: 44,
    border: `1px solid ${enErreur ? COULEURS.primaire : COULEURS.bord}`, borderRadius: 8, boxSizing: 'border-box',
    background: COULEURS.carte, color: COULEURS.texte,
  });

  const etoile = <span style={{ color: COULEURS.primaire, marginLeft: 2 }}>*</span>;
  const afficherErreur = (code) => (code
    ? <div role="alert" style={{ color: COULEURS.primaire, fontSize: '0.8rem', marginTop: 4 }}>{t(`autoInspection.${code}`)}</div>
    : null);
  const messageErreur = (cle) => afficherErreur(erreurs[cle]);

  const champIdentite = (label, champ, props = {}) => (
    <label style={{ display: 'block', marginBottom: '0.75rem', flex: '1 1 160px' }}>
      <span style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: 4 }}>{label}{etoile}</span>
      <input value={identite[champ]} onChange={(e) => majIdentite(champ, e.target.value)}
        aria-invalid={!!erreurs[champ]} style={styleSaisie(!!erreurs[champ])} {...props} />
      {messageErreur(champ)}
    </label>
  );

  const couleurOption = (opt) => {
    const s = String(opt || '').trim().toLowerCase();
    if (s === 'non') return COULEURS.primaire;
    if (s === 'oui') return COULEURS.vert;
    return COULEURS.accent;
  };

  const boutonsChoix = (opts, estActif, choisir, multiple) => (
    <div role="group" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {opts.map((opt) => {
        const actif = estActif(opt);
        const c = couleurOption(opt);
        return (
          <button type="button" key={opt} aria-pressed={actif}
            onClick={() => choisir(opt)}
            style={{ flex: '1 1 auto', minWidth: 88, minHeight: 44, padding: '0.5rem 0.75rem', fontSize: '0.9rem',
              borderRadius: 8, cursor: 'pointer', textAlign: multiple ? 'left' : 'center',
              border: `1px solid ${actif ? c : COULEURS.bord}`, background: actif ? c : COULEURS.carte,
              color: actif ? '#fff' : COULEURS.texte, fontWeight: actif ? 700 : 500 }}>
            {multiple && (actif ? '☑ ' : '☐ ')}{traduire(opt)}
          </button>
        );
      })}
    </div>
  );

  // Saisie d'une question. `val` / `setVal` : réponse courante (formulaire ou fenêtre).
  const renderSaisie = (item, cle, val, setVal, enErreur) => {
    const type = String(item.type || '').toLowerCase();
    const opts = Array.isArray(item.options) ? item.options : [];
    if (type === 'photo' || type === 'photos') {
      return (
        <div>
          {photos[cle] ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <img src={photos[cle]} alt={t('autoInspection.apercuPhoto')}
                style={{ maxWidth: 140, maxHeight: 110, borderRadius: 8, border: `1px solid ${COULEURS.bord}` }} />
              <button type="button" onClick={() => retirerPhoto(cle)}
                style={{ minHeight: 44, padding: '0.5rem 0.75rem', borderRadius: 8, cursor: 'pointer',
                  border: `1px solid ${COULEURS.bord}`, background: COULEURS.carte, color: COULEURS.texte }}>
                {t('autoInspection.retirerPhoto')}
              </button>
            </div>
          ) : (
            <input type="file" accept=".jpg,.jpeg,.png,image/jpeg,image/png"
              onChange={(e) => choisirPhoto(cle, e.target.files && e.target.files[0])}
              style={{ ...styleSaisie(enErreur), padding: '0.5rem' }} />
          )}
          <div style={{ fontSize: '0.75rem', color: COULEURS.sousTexte, marginTop: 4 }}>
            {t('autoInspection.typesFichiers')}
          </div>
        </div>
      );
    }
    if (type === 'checkbox') {
      const sel = Array.isArray(val) ? val : [];
      return boutonsChoix(opts, (opt) => sel.includes(opt),
        (opt) => setVal(sel.includes(opt) ? sel.filter((v) => v !== opt) : [...sel, opt]), true);
    }
    if (type === 'liste' || type === 'select') {
      return (
        <select value={val || ''} onChange={(e) => setVal(e.target.value)}
          aria-invalid={enErreur} style={styleSaisie(enErreur)}>
          <option value="">{t('autoInspection.choisir')}</option>
          {opts.map((o) => <option key={o} value={o}>{traduire(o)}</option>)}
        </select>
      );
    }
    if (type === 'date') {
      // 3 cases JJ / MM / AAAA (spécifications Sutton), stockées « AAAA-MM-JJ ».
      const p = partiesDate(val);
      const maj = (champ, v) => setVal(composerDate({ ...p, [champ]: String(v).replace(/\D/g, '') }));
      const completer = (champ) => { if (p[champ].length === 1) setVal(composerDate({ ...p, [champ]: `0${p[champ]}` })); };
      const boite = (champ, max, cleLibelle, largeur) => (
        <input inputMode="numeric" value={p[champ]} maxLength={max} placeholder={t(cleLibelle)}
          aria-label={t(cleLibelle)} aria-invalid={enErreur}
          onChange={(e) => maj(champ, e.target.value)} onBlur={champ === 'a' ? undefined : () => completer(champ)}
          style={{ ...styleSaisie(enErreur), width: largeur, textAlign: 'center' }} />
      );
      return (
        <div style={{ display: 'flex', gap: 8 }}>
          {boite('j', 2, 'autoInspection.jj', 64)}
          {boite('m', 2, 'autoInspection.mm', 64)}
          {boite('a', 4, 'autoInspection.aaaa', 88)}
        </div>
      );
    }
    if (type === 'nombre' || type === 'number') {
      return <input type="number" value={val ?? ''} onChange={(e) => setVal(e.target.value)}
        aria-invalid={enErreur} style={styleSaisie(enErreur)} />;
    }
    if (type === 'texte' || type === 'text' || type === 'textarea') {
      return <textarea value={val || ''} rows={2} onChange={(e) => setVal(e.target.value)}
        aria-invalid={enErreur} style={{ ...styleSaisie(enErreur), resize: 'vertical' }} />;
    }
    if (opts.length) return boutonsChoix(opts, (opt) => val === opt, (opt) => setVal(opt), false);
    return <input value={val || ''} onChange={(e) => setVal(e.target.value)}
      aria-invalid={enErreur} style={styleSaisie(enErreur)} />;
  };

  // Une question (ou un texte d'information). `ctx` : { val, setVal, erreur }.
  const renderItem = (item, cle, ctx) => {
    const type = String(item.type || '').toLowerCase();
    if (type === 'signature' || type === 'titre' || type === 'section') return null;
    const largeur = { flex: item.demi_largeur ? '1 1 240px' : '1 1 100%', minWidth: 0 };
    if (type === 'info') {
      const avert = item.variante === 'avertissement';
      return (
        <div key={cle} style={{ ...largeur, marginBottom: '0.9rem', padding: '0.75rem', borderRadius: 8, fontSize: '0.9rem',
          whiteSpace: 'pre-line', background: avert ? '#fef2f2' : '#f1f5f9', fontWeight: avert ? 600 : 400,
          border: `1px solid ${avert ? '#fecaca' : COULEURS.bord}`, color: avert ? '#b91c1c' : COULEURS.texte }}>
          <TexteAvecLiens texte={traduire(item.label)} />
        </div>
      );
    }
    const label = item.label || item.question || '';
    const labelId = `q-${cle}`;
    return (
      <div key={cle} style={{ ...largeur, marginBottom: '1rem' }}>
        <div id={labelId} style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: 4 }}>
          {traduire(label)}{item.obligatoire && etoile}
        </div>
        {item.description && (
          <div style={{ fontSize: '0.8rem', color: COULEURS.sousTexte, marginBottom: 6, whiteSpace: 'pre-line' }}>
            <TexteAvecLiens texte={traduire(item.description)} />
          </div>
        )}
        {item.schema && SCHEMAS[item.schema] && <div style={{ margin: '0.25rem 0 0.5rem' }}>{SCHEMAS[item.schema](t)}</div>}
        <div aria-labelledby={labelId}>{renderSaisie(item, cle, ctx.val, ctx.setVal, !!ctx.erreur)}</div>
        {afficherErreur(ctx.erreur)}
        {alertesDeclenchees(item, ctx.val).map((a, i) => (
          <div key={a.id || i} role="note" style={{ marginTop: 8, padding: '0.75rem', borderRadius: 8,
            background: '#fef2f2', border: '1px solid #fecaca', fontSize: '0.85rem', color: '#7f1d1d' }}>
            {a.intro && <div style={{ whiteSpace: 'pre-line', marginBottom: 6 }}>{traduire(a.intro)}</div>}
            {(a.articles || []).map((art, j) => (
              <div key={art.id || j} style={{ marginTop: j ? 8 : 0 }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>{traduire(art.titre) || art.article}</div>
                {art.description && <div style={{ whiteSpace: 'pre-line' }}>{traduire(art.description)}</div>}
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  const ctxFormulaire = (item, cle) => ({
    val: valeurDe(item, reponses, cle), setVal: (v) => repondre(cle, v), erreur: erreurs[cle],
  });

  // Valeur affichée dans le tableau d'une entrée : la réponse + ses précisions
  // conditionnelles (ex. « Autre — Grenier »).
  const celluleEntree = (s, k, item, i) => {
    const items = itemsDe(sections[s]);
    const valeurs = [reponses[cleItem(s, i, k)]];
    items.forEach((x, j) => {
      const regle = x && x.conditions_affichage && (x.conditions_affichage.regles || [])[0];
      if (regle && regle.element_id === item.id) valeurs.push(reponses[cleItem(s, j, k)]);
    });
    return valeurs.filter((v) => !vide(v))
      .map((v) => (Array.isArray(v) ? v.map(traduire).join(', ') : traduire(v))).join(' — ');
  };

  const renderSection = (s, plates) => {
    const section = sections[s];
    if (!section) return null;
    if (section.est_repetable) {
      if (!evaluerConditionsAffichage(section.conditions_affichage, plates)) return null;
      const nb = instances[s] || 0;
      const colonnes = itemsDe(section).map((it, i) => [it, i]).filter(([it]) => it && typeof it === 'object'
        && !TYPES_SANS_REPONSE.has(it.type) && !it.conditions_affichage);
      const incomplete = Object.keys(erreurs).some((c) => c.startsWith(`section_${s}_inst_`));
      const cellule = { padding: '0.5rem', borderBottom: `1px solid ${COULEURS.bord}`, textAlign: 'left', verticalAlign: 'top' };
      const petitBouton = (couleur) => ({ minHeight: 40, padding: '0.3rem 0.8rem', borderRadius: 8, cursor: 'pointer',
        border: `1px solid ${couleur}`, background: COULEURS.carte, color: couleur, marginRight: 6, marginBottom: 4 });
      const boutonsEntree = (sIdx, k) => (
        <>
          <button type="button" onClick={() => ouvrirFenetre(sIdx, k)} style={petitBouton(COULEURS.accent)}
            aria-label={t('autoInspection.modifierEntreeAria', { n: k + 1 })}>{t('autoInspection.modifier')}</button>
          <button type="button" onClick={() => supprimerInstance(sIdx, k)} style={petitBouton(COULEURS.primaire)}
            aria-label={t('autoInspection.supprimerEntreeAria', { n: k + 1 })}>{t('autoInspection.supprimer')}</button>
        </>
      );
      return carte(
        <>
          <h3 style={{ marginTop: 0 }}>{traduire(section.titre)}</h3>
          {section.description && <p style={{ fontSize: '0.85rem', color: COULEURS.sousTexte }}>{traduire(section.description)}</p>}
          {nb === 0 && (
            <p style={{ fontSize: '0.85rem', color: COULEURS.sousTexte, fontStyle: 'italic', textAlign: 'center' }}>
              {t('autoInspection.aucuneEntree')}
            </p>
          )}
          {nb > 0 && ecranLarge && (
            <div style={{ overflowX: 'auto', marginBottom: '0.75rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr style={{ background: COULEURS.fond }}>
                    {colonnes.map(([it, i]) => <th key={i} scope="col" style={{ ...cellule, fontWeight: 600 }}>{traduire(it.label)}</th>)}
                    <th scope="col" style={cellule}><span style={{ position: 'absolute', left: -9999 }}>{t('autoInspection.actions')}</span></th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: nb }, (_, k) => (
                    <tr key={k}>
                      {colonnes.map(([it, i]) => <td key={i} style={cellule}>{celluleEntree(s, k, it, i)}</td>)}
                      <td style={{ ...cellule, whiteSpace: 'nowrap' }}>{boutonsEntree(s, k)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {nb > 0 && !ecranLarge && Array.from({ length: nb }, (_, k) => (
            <div key={k} style={{ border: `1px solid ${COULEURS.bord}`, borderRadius: 10, padding: '0.75rem', marginBottom: '0.75rem',
              fontSize: '0.85rem' }}>
              <strong>#{k + 1}</strong>
              {colonnes.map(([it, i]) => (
                <div key={i} style={{ marginTop: 6 }}>
                  <div style={{ color: COULEURS.sousTexte, fontSize: '0.75rem' }}>{traduire(it.label)}</div>
                  <div style={{ fontWeight: 600 }}>{celluleEntree(s, k, it, i) || '—'}</div>
                </div>
              ))}
              <div style={{ marginTop: 8 }}>{boutonsEntree(s, k)}</div>
            </div>
          ))}
          {incomplete && afficherErreur('entreeIncomplete')}
          <button type="button" onClick={() => ouvrirFenetre(s)}
            style={{ minHeight: 44, padding: '0.6rem 1rem', borderRadius: 8, cursor: 'pointer', fontWeight: 600,
              border: 'none', background: COULEURS.accent, color: '#fff' }}>
            {traduire(section.bouton_ajouter_label) || t('autoInspection.ajouter')}
          </button>
        </>, `s${s}`);
    }
    const contenus = [];
    parcourirSection(sections, s, reponses, 0, plates, (item, cle) => {
      contenus.push(renderItem(item, cle, ctxFormulaire(item, cle)));
    });
    if (!contenus.length) return null;
    return carte(
      <>
        {section.titre && <h3 style={{ marginTop: 0 }}>{traduire(section.titre)}</h3>}
        {section.description && <p style={{ fontSize: '0.85rem', color: COULEURS.sousTexte }}>{traduire(section.description)}</p>}
        <div style={{ display: 'flex', flexWrap: 'wrap', columnGap: 16 }}>{contenus}</div>
      </>, `s${s}`);
  };

  const renderFenetre = () => {
    if (!fenetre) return null;
    const section = sections[fenetre.s] || {};
    const majValeur = (i) => (v) => setFenetre((f) => {
      const erreursRestantes = { ...f.erreurs };
      delete erreursRestantes[i];
      return { ...f, valeurs: { ...f.valeurs, [i]: v }, erreurs: erreursRestantes };
    });
    return (
      <Dialog open onOpenChange={(ouvert) => { if (!ouvert) setFenetre(null); }}>
        <DialogContent style={{ maxHeight: '90vh', overflowY: 'auto', background: COULEURS.carte, color: COULEURS.texte }}>
          <DialogHeader>
            <DialogTitle>{traduire(section.titre_fenetre || section.bouton_ajouter_label) || t('autoInspection.ajouter')}</DialogTitle>
            <DialogDescription>{t('autoInspection.indiqueNecessaires')}</DialogDescription>
          </DialogHeader>
          <div>
            {itemsVisiblesFenetre(fenetre).map(([it, i]) => renderItem(it, `fenetre-${fenetre.s}-${i}`,
              { val: valeurDe(it, fenetre.valeurs, i), setVal: majValeur(i), erreur: fenetre.erreurs[i] }))}
          </div>
          <button type="button" onClick={validerFenetre}
            style={{ minHeight: 44, padding: '0.6rem 1rem', borderRadius: 8, cursor: 'pointer', fontWeight: 700,
              border: 'none', background: COULEURS.accent, color: '#fff' }}>
            {traduire(section.bouton_valider_label) || t('autoInspection.ajouter')}
          </button>
        </DialogContent>
      </Dialog>
    );
  };

  if (envoye) {
    return carte(
      <div style={{ textAlign: 'center', padding: '0.75rem' }}>
        <div style={{ fontSize: '2.5rem' }}>✅</div>
        <h2>{t('autoDeclaration.merciTitre')}</h2>
        <p style={{ color: COULEURS.sousTexte }}>{t('autoInspection.merciTexte')}</p>
      </div>, 'merci');
  }

  const etape = etapes[etapeIdx] || { titre: '', sections: [] };
  const plates = valeursPlates(sections, reponses);
  const pourcentage = nbEtapes ? Math.round(((etapeIdx + 1) / nbEtapes) * 100) : 100;
  const derniere = etapeIdx >= nbEtapes - 1;

  return (
    <div ref={hautRef}>
      <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
        {config.logo_url && (
          <img src={config.logo_url} alt={config.service_nom || t('autoDeclaration.logo')}
            style={{ maxHeight: 84, maxWidth: '70%', objectFit: 'contain', marginBottom: '0.75rem' }} />
        )}
        <h1 style={{ margin: '0 0 0.25rem' }}>{traduire(config.titre) || t('autoInspection.titreDefaut')}</h1>
        <p style={{ color: COULEURS.sousTexte, margin: 0 }}>{config.service_nom}</p>
      </div>

      {restaure && (
        <div role="status" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10,
          justifyContent: 'space-between', marginBottom: '1rem', padding: '0.75rem 1rem', borderRadius: 10,
          background: '#eff6ff', border: '1px solid #bfdbfe', fontSize: '0.85rem', color: '#1e3a8a' }}>
          <span>{t('autoInspection.brouillonRestaure')}</span>
          <button type="button" onClick={recommencer}
            style={{ minHeight: 40, padding: '0.4rem 0.8rem', borderRadius: 8, cursor: 'pointer',
              border: '1px solid #93c5fd', background: COULEURS.carte, color: '#1e3a8a', fontWeight: 600 }}>
            {t('autoInspection.recommencer')}
          </button>
        </div>
      )}

      {etapeIdx === 0 && config.introduction && carte(
        <div style={{ fontSize: '0.9rem', whiteSpace: 'pre-line', color: COULEURS.texte }}>{traduire(config.introduction)}</div>, 'intro')}

      {/* En-tête affiché à chaque étape (spécifications Sutton). */}
      <div style={{ fontSize: '0.8rem', color: COULEURS.sousTexte, margin: '0 0 0.75rem' }}>
        <p style={{ margin: '0 0 0.25rem' }}>
          {t('autoInspection.obligatoiresAvant')} <span style={{ color: COULEURS.primaire }}>*</span> {t('autoInspection.obligatoiresApres')}
        </p>
        <p style={{ margin: 0 }}>{t('autoInspection.indiqueNecessaires')}</p>
      </div>

      {/* Progression */}
      <div style={{ marginBottom: '1rem' }} aria-live="polite">
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', fontWeight: 600, marginBottom: 6 }}>
          <span>{t('autoInspection.etapeXsurN', { n: etapeIdx + 1, total: nbEtapes, titre: traduire(etape.titre) })}</span>
          <span>{pourcentage}%</span>
        </div>
        <div role="progressbar" aria-valuenow={pourcentage} aria-valuemin={0} aria-valuemax={100}
          style={{ height: 8, background: COULEURS.bord, borderRadius: 999, overflow: 'hidden' }}>
          <div style={{ width: `${pourcentage}%`, height: '100%', background: COULEURS.primaire, transition: 'width .2s' }} />
        </div>
      </div>

      {etapeIdx === 0 && carte(
        <>
          <div style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: 6 }}>{t('autoInspection.nomRepondant')}{etoile}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0 12px' }}>
            {champIdentite(t('autoInspection.prenom'), 'prenom', { autoComplete: 'given-name' })}
            {champIdentite(t('autoInspection.nom'), 'nom', { autoComplete: 'family-name' })}
          </div>
          <div style={{ fontSize: '0.95rem', fontWeight: 600, margin: '0.25rem 0 6px' }}>{t('autoInspection.adresse')}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0 12px' }}>
            <label style={{ display: 'block', marginBottom: '0.75rem', flex: '0 1 140px' }}>
              <span style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: 4 }}>{t('autoInspection.numeroCivique')}{etoile}</span>
              <input value={identite.numero_civique} maxLength={10} autoComplete="off"
                onChange={(e) => majAdresse('numero_civique', e.target.value)}
                aria-invalid={!!erreurs.numero_civique} style={styleSaisie(!!erreurs.numero_civique)} />
              <div style={{ fontSize: '0.75rem', color: COULEURS.sousTexte, marginTop: 4 }}>
                {t('autoInspection.compteurCaracteres', { n: identite.numero_civique.length, max: 10 })}
              </div>
              {messageErreur('numero_civique')}
            </label>
            <label style={{ display: 'block', marginBottom: '0.75rem', flex: '1 1 200px', position: 'relative' }}>
              <span style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: 4 }}>{t('autoInspection.rue')}{etoile}</span>
              <input value={identite.rue} placeholder={t('autoInspection.ruePlaceholder')} autoComplete="off"
                onChange={(e) => majAdresse('rue', e.target.value)}
                aria-invalid={!!erreurs.rue} style={styleSaisie(!!erreurs.rue)} />
              {messageErreur('rue')}
              {suggestions.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 5,
                  background: COULEURS.carte, border: `1px solid ${COULEURS.bord}`, borderRadius: 8,
                  marginTop: -6, maxHeight: 220, overflowY: 'auto', boxShadow: '0 8px 20px rgba(0,0,0,.08)' }}>
                  {suggestions.map((it) => (
                    <button type="button" key={it.id} onClick={() => choisirAdresse(it)}
                      style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', minHeight: 44,
                        background: COULEURS.carte, padding: '0.6rem 0.75rem', cursor: 'pointer',
                        fontSize: '0.95rem', borderBottom: `1px solid ${COULEURS.fond}` }}>
                      {it.adresse_civique} <span style={{ color: COULEURS.sousTexte }}>— {it.ville}</span>
                    </button>
                  ))}
                </div>
              )}
            </label>
          </div>
          {champIdentite(t('autoInspection.courriel'), 'courriel', { type: 'email', autoComplete: 'email',
            placeholder: t('autoInspection.courrielPlaceholder') })}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0 12px' }}>
            {champIdentite(t('autoInspection.telephone'), 'telephone', { type: 'tel', autoComplete: 'tel', placeholder: '(999) 999-9999',
              onChange: (e) => majIdentite('telephone', formaterTelephone(e.target.value)) })}
            {champIdentite(t('autoInspection.telephoneUrgence'), 'telephone_urgence', { type: 'tel',
              placeholder: '(999) 999-9999',
              onChange: (e) => majIdentite('telephone_urgence', formaterTelephone(e.target.value)) })}
          </div>
          {/* Honeypot anti-bot : nom NEUTRE + display:none (cf. AutoDeclarationPublique). */}
          <input type="text" name="ne_pas_remplir" tabIndex={-1} autoComplete="off" aria-hidden="true"
            value={neRienRemplir} onChange={(e) => setNeRienRemplir(e.target.value)} style={{ display: 'none' }} />
        </>, 'identite')}

      {etape.sections.map((s) => renderSection(s, plates))}
      {renderFenetre()}

      {erreurPhoto && <p role="alert" style={{ color: COULEURS.primaire, textAlign: 'center' }}>{t(erreurPhoto)}</p>}
      {erreur && <p role="alert" style={{ color: COULEURS.primaire, textAlign: 'center' }}>{t(erreur)}</p>}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        {etapeIdx > 0 && (
          <button type="button" onClick={precedent}
            style={{ flex: '1 1 160px', minHeight: 48, padding: '0.8rem', fontSize: '1rem', fontWeight: 600, borderRadius: 10,
              cursor: 'pointer', border: `1px solid ${COULEURS.bord}`, background: COULEURS.carte, color: COULEURS.texte }}>
            {t('autoInspection.precedent', { titre: traduire(etapes[etapeIdx - 1].titre) })}
          </button>
        )}
        {!derniere ? (
          <button type="button" onClick={suivant}
            style={{ flex: '1 1 160px', minHeight: 48, padding: '0.8rem', fontSize: '1rem', fontWeight: 700, borderRadius: 10,
              cursor: 'pointer', border: 'none', color: '#fff', background: COULEURS.accent }}>
            {t('autoInspection.suivant', { titre: traduire(etapes[etapeIdx + 1].titre) })}
          </button>
        ) : (
          <button type="button" onClick={envoyer} disabled={envoi}
            style={{ flex: '1 1 160px', minHeight: 48, padding: '0.8rem', fontSize: '1.05rem', fontWeight: 700, borderRadius: 10,
              border: 'none', color: '#fff', cursor: envoi ? 'not-allowed' : 'pointer',
              background: envoi ? '#94a3b8' : COULEURS.vert }}>
            {envoi ? t('autoDeclaration.envoi') : t('autoInspection.envoyer')}
          </button>
        )}
      </div>
      <p style={{ fontSize: '0.75rem', color: COULEURS.sousTexte, textAlign: 'center', marginTop: '0.75rem' }}>
        {t('autoDeclaration.mentionVerification')}
      </p>
    </div>
  );
};

export default AutoInspectionParEtapes;
